import React, { useEffect, useRef } from "react";
import { getUserPrefersLang } from "../../../shared/helpersMethod";
import { useAppSelector } from "../../../hooks/useReduxMethods";

interface ITabbyPromoCode {
  price: number;
}

// Global flag to track script loading state
let isTabbyScriptLoaded = false;
let isTabbyScriptLoading = false;
let tabbyScriptPromise: Promise<void> | null = null;

const TabbyPromoDesktop: React.FC<ITabbyPromoCode> = (
  props: ITabbyPromoCode
) => {
  const usrLang = getUserPrefersLang() ?? "en";
  const { price } = props;
  const componentMounted = useRef(true);
  const { paymentData } = useAppSelector((state) => state.paymentGateways);
  const loadTabbyScript = (): Promise<void> => {
    // Return existing promise if script is already loading
    if (tabbyScriptPromise) {
      return tabbyScriptPromise;
    }

    // Return resolved promise if script is already loaded
    if (isTabbyScriptLoaded) {
      return Promise.resolve();
    }

    // Check if script is already in DOM
    const existingScript = document.querySelector(
      'script[src="https://checkout.tabby.ai/tabby-promo.js"]'
    );
    if (existingScript) {
      isTabbyScriptLoaded = true;
      return Promise.resolve();
    }

    // Create new promise for script loading
    tabbyScriptPromise = new Promise((resolve, reject) => {
      isTabbyScriptLoading = true;

      const script = document.createElement("script");
      script.src = "https://checkout.tabby.ai/tabby-promo.js";
      script.async = true;

      script.onload = () => {
        isTabbyScriptLoaded = true;
        isTabbyScriptLoading = false;
        resolve();
      };

      script.onerror = () => {
        isTabbyScriptLoading = false;
        tabbyScriptPromise = null; // Reset promise so it can be retried
        reject(new Error("Failed to load Tabby script"));
      };

      document.head.appendChild(script); // Use head instead of body for better performance
    });

    return tabbyScriptPromise;
  };

  const initializeTabbyPromo = () => {
    if (window.TabbyPromo && componentMounted.current) {
      try {
        new window.TabbyPromo({
          selector: "#tabby-promo",
          price: price,
          currency: "AED",
          installmentsCount: 4,
          lang: usrLang,
          publicKey: paymentData?.credentials?.public_key, // required, Public Key
          merchantCode: paymentData?.credentials?.merchant_code, // required
        });
      } catch (error) {
        console.error("Failed to initialize Tabby Promo:", error);
      }
    }
  };

  useEffect(() => {
    const loadAndInitialize = async () => {
      try {
        await loadTabbyScript();
        initializeTabbyPromo();
      } catch (error) {
        console.error("Failed to load Tabby script:", error);
      }
    };

    loadAndInitialize();

    // Cleanup function
    return () => {
      componentMounted.current = false;
    };
  }, []); // Only run once on mount

  // Reinitialize when price or language changes
  useEffect(() => {
    if (isTabbyScriptLoaded) {
      initializeTabbyPromo();
    }
  }, [price, usrLang]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      componentMounted.current = false;
    };
  }, []);

  return (
    <div>
      <div id="tabby-promo"></div>
    </div>
  );
};

export default TabbyPromoDesktop;
